import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  Card,
  CardContent,
  Avatar,
  Button,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { getDoc, doc, deleteDoc, setDoc } from "firebase/firestore";
import { db } from "../../../firebase";
import { useLocation } from "react-router-dom";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import DynamicUploadModal from "../../../components/UploadModal";

const Services = () => {
  const [openModal, setOpenModal] = useState(false);
  const [uploadedImageUrl, setUploadedImageUrl] = useState("");
  const [data, setData] = useState({});
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const uid = params.get("uid");
    if (uid) {
      fetchData(uid);
    }
  }, [location.search]);

  const fetchData = async (uid) => {
    try {
      const docRef = doc(db, "services", uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setData(docSnap.data());
      } else {
        console.log("No such document!");
      }
    } catch (error) {
      console.error("Error fetching document:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleModalClose = () => {
    setOpenModal(false);
  };

  const handleUploadSuccess = (url) => {
    setUploadedImageUrl(url);
    setData((prevData) => ({
      ...prevData,
      imageUrl: url,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const uid = data.id;
      const docRef = doc(db, "services", uid);
      // Use the uploadedImageUrl if it's not empty, otherwise use the old imageUrl from data
      const imageUrlToSave = uploadedImageUrl || data.imageUrl;

      await setDoc(docRef, { ...data, imageUrl: imageUrlToSave });
      setAlertSeverity("success");
      setAlertMessage("Data saved successfully!");
    } catch (err) {
      console.error("Error saving data:", err);
      setAlertSeverity("error");
      setAlertMessage(`Error: ${err.message}`);
    } finally {
      setShowAlert(true);
    }
};


  const handleDelete = async () => {
    try {
      const uid = data.id;
      await deleteDoc(doc(db, "services", uid));
      setAlertSeverity("success");
      setAlertMessage("Service deleted successfully!");
      setData({});
      handleDeleteDialogClose();
    } catch (err) {
      console.error("Error deleting service:", err);
      setAlertSeverity("error");
      setAlertMessage(`Error: ${err.message}`);
    } finally {
      setShowAlert(true);
    }
  };

  const handleDeleteDialogOpen = () => {
    setShowDeleteDialog(true);
  };

  const handleDeleteDialogClose = () => {
    setShowDeleteDialog(false);
    setDeleteConfirmation("");
  };

  const handleDeleteConfirmationChange = (e) => {
    setDeleteConfirmation(e.target.value);
  };

  const handleAlertClose = () => {
    setShowAlert(false);
  };

  return (
    <div className="App">
      <main style={{ padding: isMobile ? "10px 5px" : "20px 5px" }}>
        <Grid container justifyContent="center">
          <Grid item xs={12} sm={10} md={8} lg={6}>
            <Avatar
              alt="Service Image"
              src={data.imageUrl || "/placeholder.jpg"}
              style={{
                width: 100,
                height: 100,
                margin: "0 auto",
                marginBottom: 20,
              }}
            />
            <Card style={{ margin: "0 auto" }}>
              <CardContent>
                <Typography
                  variant="h6"
                  color="#272727"
                  align="center"
                  gutterBottom
                >
                  Service Details View
                </Typography>
                <form onSubmit={handleSave}>
                  <Grid container spacing={isMobile ? 2 : 4}>
                    <Grid item xs={12}>
                      <TextField
                        label="ID"
                        name="id"
                        value={data.id || ""}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        disabled
                      />
                      <TextField
                        label="Title"
                        name="title"
                        value={data.title || ""}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        style={{ marginTop: "16px" }}
                      />
                      <TextField
                        label="Subtitle"
                        name="subtitle"
                        value={data.subtitle || ""}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        style={{ marginTop: "16px" }}
                      />
                      <TextField
                        label="Description"
                        name="description"
                        value={data.description || ""}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={isMobile ? 2 : 4}
                        style={{ marginTop: "16px" }}
                      />
                      <TextField
                        label="Message"
                        name="message"
                        value={data.message || ""}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={isMobile ? 2 : 4}
                        style={{ marginTop: "16px" }}
                      />
                      <TextField
                        label="Phone Number"
                        name="phoneno"
                        value={data.phoneno || ""}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        style={{ marginTop: "16px" }}
                      />
                      <TextField
                        label="Service Type"
                        name="serviceType"
                        value={data.serviceType || ""}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        style={{ marginTop: "16px" }}
                      />
                      <TextField
                        label="URL"
                        name="url"
                        value={data.url || ""}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        style={{ marginTop: "16px" }}
                      />
                      <TextField
                        label="Image URL"
                        name="imageUrl"
                        value={uploadedImageUrl}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        style={{ marginTop: "16px" }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <IconButton
                        onClick={() => setOpenModal(true)}
                        color="primary"
                        aria-label="upload image"
                      >
                        <PhotoCamera />
                      </IconButton>
                    </Grid>
                    <Grid container justifyContent="center">
                      <Grid item>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          sx={{
                            borderRadius: 15,
                            boxShadow: "none",
                            width: isMobile ? "100%" : "200px",
                            marginTop: "16px",
                          }}
                        >
                          Update
                        </Button>
                        <Button
                          variant="contained"
                          color="secondary"
                          sx={{
                            borderRadius: 15,
                            boxShadow: "none",
                            width: isMobile ? "100%" : "200px",
                            marginTop: "16px",
                            marginLeft: "16px",
                          }}
                          onClick={handleDeleteDialogOpen}
                        >
                          Delete
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </form>
                <DynamicUploadModal
                  open={openModal}
                  onClose={handleModalClose}
                  uploadPreset="bookMyDr"
                  folderName="Madecare"
                  maxSize={5 * 1024 * 1024}
                  onUploadSuccess={handleUploadSuccess}
                />
              </CardContent>
            </Card>
            <Snackbar
              open={showAlert}
              autoHideDuration={6000}
              onClose={handleAlertClose}
            >
              <Alert
                onClose={handleAlertClose}
                severity={alertSeverity}
                sx={{ width: "100%" }}
              >
                {alertMessage}
              </Alert>
            </Snackbar>
            <Dialog
              open={showDeleteDialog}
              onClose={handleDeleteDialogClose}
            >
              <DialogTitle>Delete Service</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Are you sure you want to delete this service? Type "DELETE" to confirm.
                </DialogContentText>
                <TextField
                  autoFocus
                  margin="dense"
                  label="Type DELETE"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={deleteConfirmation}
                  onChange={handleDeleteConfirmationChange}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleDeleteDialogClose} color="primary">
                  Cancel
                </Button>
                <Button
                  onClick={handleDelete}
                  color="secondary"
                  disabled={deleteConfirmation !== "DELETE"}
                >
                  Delete
                </Button>
              </DialogActions>
            </Dialog>
          </Grid>
        </Grid>
      </main>
    </div>
  );
};

export default Services;
